import React, { useEffect, useState } from "react";
import { FaEdit, FaSort, FaSortUp, FaSortDown, FaStar } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { BiSolidImageAdd } from "react-icons/bi";
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import Toast from '../../components/Toast/Toast';
import { getProductsAPI, deleteProductAPI, updateProductsAPI } from '../../utils/api';
import { BASE_URL } from '../../utils/configConstants';
import Sidebar from "../Sidebar/Sidebar";
import "./Product.css";
import { useNavigate, useLocation } from 'react-router-dom';

const Product = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [stockFilter, setStockFilter] = useState(location.state?.filter === 'out_of_stock' ? 'out_of_stock' : 'all');

  const PAGE_LIMIT = 10;

  const buildImageUrl = (path) => {
    if (!path) return "";

    // Handle different image path structures
    if (typeof path === 'string') {
      return /^https?:\/\//i.test(path) ? path : `${BASE_URL}${path}`;
    }

    // If path is an object, try different possible structures
    if (typeof path === 'object') {
      // Try different possible image properties
      const possiblePaths = [
        path.image,
        path.url,
        path.path,
        path.src,
        path.thumbnail,
        path.original
      ];

      for (const imgPath of possiblePaths) {
        if (imgPath && typeof imgPath === 'string') {
          return /^https?:\/\//i.test(imgPath) ? imgPath : `${BASE_URL}${imgPath}`;
        }
      }
    }

    return "";
  };

  const getProductImage = (product) => {
    // Try different possible image structures
    const possibleImagePaths = [
      product?.image?.image,
      product?.image,
      product?.images?.[0]?.image,
      product?.images?.[0],
      product?.thumbnail,
      product?.main_image
    ];

    for (const imagePath of possibleImagePaths) {
      const imageUrl = buildImageUrl(imagePath);
      if (imageUrl) {
        return imageUrl;
      }
    }

    // Return a simple placeholder image if no image found
    return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50' y='50' font-family='Arial' font-size='12' fill='%239ca3af' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";
  };

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const fetchProducts = async (categoryId = null, search = "", currentPage = 1) => {
    try {
      setLoading(true);
      setError(null);
      const reqData = {
        category_id: categoryId,
        search: search.trim(),
        page: currentPage,
        limit: PAGE_LIMIT,
        ...(stockFilter === 'out_of_stock' && { out_of_stock: true })
      };

      const response = await getProductsAPI(reqData);

      if (response?.status === 200) {
        const list = response.data;
        const totalPagesFromApi = response?.pagination?.total_pages || 1;
        const currentPageFromApi = response?.pagination?.current_page || currentPage;

        setProducts(list);
        setTotalPages(totalPagesFromApi);
        setPage(currentPageFromApi);
      } else {
        setError(response?.message || 'Failed to fetch products');
        setProducts([]);
      }
    } catch (err) {
      console.log('Fetch error:', err);
      setError('Failed to fetch products. Please try again.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if location has filter out_of_stock
    if (location.state?.filter === 'out_of_stock') {
      setStockFilter('out_of_stock');
    }
  }, [location.state]);

  useEffect(() => {
    // Check if we have category filter from URL query parameters
    const queryParams = new URLSearchParams(window.location.search);
    const categoryId = queryParams.get("categoryId");
    const refresh = queryParams.get("refresh");

    if (categoryId) {
      setCategoryFilter(categoryId);
      fetchProducts(categoryId, searchTerm, page);
    } else {
      setCategoryFilter(null);
      fetchProducts(null, searchTerm, page);
    }

    if (refresh) {
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('refresh');
      window.history.replaceState({}, '', newUrl);
    }
  }, [page, searchTerm, stockFilter]);

  // Add focus event listener to refresh products when user returns to the page
  useEffect(() => {
    const handleFocus = () => {
      const queryParams = new URLSearchParams(window.location.search);
      const categoryId = queryParams.get("categoryId");
      fetchProducts(categoryId, searchTerm, page);
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [searchTerm, page]);

  const totalEntries = products.length;
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_LIMIT;
  const endIdx = startIdx + products.length;

  const handleEdit = (product) => {
    const url = categoryFilter
      ? `/edit-product/${product._id}?catId=${categoryFilter}&filter=${stockFilter}`
      : `/edit-product/${product._id}?filter=${stockFilter}`;
    window.location.href = url;
  };

  const handleAddNew = () => {
    const url = categoryFilter
      ? `/add-product/${categoryFilter}?filter=${stockFilter}`
      : `/add-product?filter=${stockFilter}`;
    window.location.href = url;
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      // Validate product data
      if (!productToDelete?._id) {
        showToast("Invalid product data", "error");
        return;
      }

      const reqData = { product_id: productToDelete._id };
      const response = await deleteProductAPI(reqData);

      if (response?.status === 200) {
        setShowDeleteModal(false);
        setProductToDelete(null);
        showToast(`Product "${productToDelete?.name}" deleted successfully!`, "success");
        await fetchProducts(categoryFilter, searchTerm, page); // Refresh the list
      } else if (response?.message) {
        showToast(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error', "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    }
  };

  const handleToggleActive = async (productId, newActive) => {
    try {
      const reqData = {
        product_id: productId,
        active: newActive
      };
      const response = await updateProductsAPI(reqData);
      if (response?.status === 200) {
        await fetchProducts(categoryFilter, searchTerm, page);
      } else if (response?.message) {
        showToast(typeof response?.message === 'string' ? response?.message : 'Failed due to internal error', 'error');
      } else {
        showToast('Failed due to internal error', 'error');
      }
    } catch (err) {
      showToast('Failed due to internal error', 'error');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField === field) {
      return sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />;
  };

  const getSortedData = () => {
    const sorted = [...products].sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';

      // Convert to string for comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();

      if (sortOrder === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
    return sorted;
  };

  const sortedProducts = getSortedData();

  const getPageItems = () => {
    const items = [];
    const current = Math.max(1, Math.min(page, totalPages));
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) items.push(i);
      return items;
    }

    items.push(1);
    const showLeftDots = current > 4;
    const showRightDots = current < totalPages - 3;

    if (showLeftDots) items.push('dots-left');

    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);
    for (let i = start; i <= end; i++) items.push(i);

    if (showRightDots) items.push('dots-right');

    items.push(totalPages);
    return items;
  };

  return (
    <>
      <Toast toast={toast} />

      <div className="Product-layout">
        <Sidebar />
        <div className="Product-main-content">
          <div className="Product-header-row">
            <div className="header-left">
              <div className="filter-buttons">
                <button
                  type="button"
                  className={`filter-btn ${stockFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setStockFilter('all')}
                  aria-label="All Products"
                >
                  <span>All</span>
                </button>
                <button
                  type="button"
                  className={`filter-btn ${stockFilter === 'out_of_stock' ? 'active' : ''}`}
                  onClick={() => setStockFilter('out_of_stock')}
                  aria-label="Out of Stock Products"
                >
                  <span>Out of Stock</span>
                </button>
              </div>
            </div>
            <div className="header-right">
              <input
                type="text"
                className="search-input"
                placeholder="Search by product name, description, or category"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search products"
              />
              <button
                type="button"
                className="insert-btn"
                onClick={handleAddNew}
                aria-label="Add Product"
              >
                <span>Add Product</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading products...</p>
            </div>
          ) : error ? (
            <div className="loading-container">
              <div style={{ color: '#dc2626', fontSize: '1.25rem', marginBottom: '1rem' }}>⚠️</div>
              <p style={{ color: '#dc2626', fontSize: '1.125rem' }}>{error}</p>
            </div>
          ) : totalEntries === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🛍️</div>
              <h3>No Products Found</h3>
              {stockFilter === 'out_of_stock' ? (
                <p>There are no out of stock products in the system.</p>
              ) : (
                <>
                  <p>There are no products in the system yet.</p>
                  <p>Click "Add Product" to create the first one.</p>
                </>
              )}
            </div>
          ) : products.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔎</div>
              <h3>No results</h3>
              <p>We couldn't find any product matching "{searchTerm}".</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="Product-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th
                        className="sortable-header"
                      // onClick={() => handleSort("name")}
                      >
                        <div className="header-content">
                          Product Name
                          {/* <span className="sort-icon">
                            {getSortIcon("name")}
                          </span> */}
                        </div>
                      </th>
                      <th>Image</th>
                      <th>Publish</th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product, idx) => (
                      <tr
                        key={product._id || `product-${idx}`}
                        className="product-row"
                        onClick={() => {
                          const source = categoryFilter ? 'category' : 'product';
                          const url = categoryFilter
                            ? `/product-details/${product._id}?source=${source}&categoryId=${categoryFilter}&filter=${stockFilter}`
                            : `/product-details/${product._id}?source=${source}&filter=${stockFilter}`;
                          navigate(url);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{startIdx + idx + 1}</td>
                        <td>{product.name || ''}</td>
                        <td>
                          <img
                            src={getProductImage(product)}
                            alt={product?.name}
                            className="product-img"
                            onError={(e) => {
                              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50' y='50' font-family='Arial' font-size='12' fill='%239ca3af' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";
                            }}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className={`product-switch${product.active ? ' active' : ''}`}
                            onClick={e => {
                              e.stopPropagation();
                              setToggleTarget({
                                id: product._id,
                                active: !product.active,
                                name: product.name,
                                oldActive: product.active
                              });
                              setShowToggleModal(true);
                            }}
                          >
                            <span className="switch-slider" />
                          </button>
                        </td>
                        <td>
                          <div className="product-action-btn-row" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="product-action-btn product-edit-btn"
                              title="Edit Product"
                              onClick={() => handleEdit(product)}
                              aria-label="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button
                              type="button"
                              className="product-action-btn product-image-btn"
                              title="Upload Images"
                              onClick={() => {
                                const url = categoryFilter
                                  ? `/product-image-upload/${product._id}?categoryId=${categoryFilter}&filter=${stockFilter}`
                                  : `/product-image-upload/${product._id}?filter=${stockFilter}`;
                                window.location.href = url;
                              }}
                              aria-label="Upload Images"
                            >
                              <BiSolidImageAdd />
                            </button>
                            <button
                              type="button"
                              className="product-action-btn product-delete-btn"
                              title="Delete Product"
                              onClick={() => handleDeleteClick(product)}
                              aria-label="Delete"
                            >
                              <MdDelete />
                            </button>
                            <button
                              type="button"
                              className="product-action-btn product-review-btn"
                              title="View Reviews"
                              onClick={() => navigate(`/product-rating-review/${product._id}?filter=${stockFilter}`)}
                              aria-label="View Reviews"
                            >
                              <FaStar />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="table-footer">
                <div className="footer-left">
                  <span>
                    Showing {products.length > 0 ? startIdx + 1 : 0} to {endIdx} • Page {page} of {totalPages}
                  </span>
                </div>
                <div className="pagination">
                  <button
                    type="button"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  {getPageItems().map((item, idx) => (
                    typeof item === 'number' ? (
                      <button
                        type="button"
                        key={`p-${item}`}
                        className={page === item ? 'active' : ''}
                        onClick={() => handlePageChange(item)}
                        aria-current={page === item ? 'page' : undefined}
                      >
                        {item}
                      </button>
                    ) : (
                      <span key={`dots-${idx}`} className="ellipsis" aria-hidden="true">…</span>
                    )
                  ))}
                  <button
                    type="button"
                    disabled={page === totalPages}
                    onClick={() => handlePageChange(page + 1)}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}

          <ConfirmationModal
            visible={showDeleteModal}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            title="Confirm Deletion"
            message={`Are you sure you want to delete the Product "${productToDelete?.name || ''}"?`}
            confirmText="Delete"
          />
          <ConfirmationModal
            visible={showToggleModal}
            onConfirm={async () => {
              if (toggleTarget) {
                await handleToggleActive(toggleTarget.id, toggleTarget.active);
              }
              setShowToggleModal(false);
              setToggleTarget(null);
            }}
            onCancel={() => {
              setShowToggleModal(false);
              setToggleTarget(null);
            }}
            title={toggleTarget && toggleTarget.oldActive ? 'Inactive Product' : 'Active Product'}
            message={toggleTarget ? (
              toggleTarget.oldActive
                ? `Are you sure you want to Inactive the product "${toggleTarget.name}"?`
                : `Are you sure you want to activate the product "${toggleTarget.name}"?`
            ) : ''}
            confirmText={toggleTarget && toggleTarget.oldActive ? 'Inactive' : 'Active'}
            confirmBtnClass="product-confirm-btn"
          />
        </div>
      </div>
    </>
  );
};

export default Product; 